// StockImageLibrary — a reusable curated-image picker surfaced inside the shared
// media picker (Shared/MediaSelection "Stock" source) so the curated set is
// selectable as a fill / wallpaper / portrait / image-block, not just a wallpaper.
// Deck: concepts/procedural-libraries/05-image-library ("Preset images" grid).
//
// Selecting a tile routes the chosen image THROUGH the shared moderation wrapper
// (routeImageThroughModeration, source "stock") — the same choke point uploads use
// — before it is handed back to the consumer via onSelect. The verdict is HONORED:
// an un-approved selection is refused (NEM F2). Stock items are curated/pre-vetted
// so the wrapper approves them without a network round-trip today, but the selection
// still passes through — and now respects — the one governance point.
//
// Additive + flag-gated: this component only renders when MediaSelection mounts the
// "Stock" tab (STOCK_IMAGE_LIBRARY_ENABLED + image type). Nothing here runs when the
// flag is OFF.

import React, { useMemo, useRef, useState } from "react";
import styled from "styled-components";
import {
  STOCK_IMAGE_ITEMS,
  toStockInsertValue,
  isStockItemSelected,
} from "./stockImageLibrary.logic";
import { routeImageThroughModeration, MODERATION_SOURCE } from "../../utils/mediaModeration";
import { focusRing } from "../../theme/tokens";

// Grid column count — kept in sync with grid-template-columns below so Arrow
// Up/Down move focus by exactly one row (roving-tabindex listbox nav, NEM F1).
const GRID_COLS = 4;

// Keyboard maps for the roving-tabindex listbox (NEM F1).
const SELECT_KEYS = new Set(["Enter", " ", "Spacebar"]);
const ARROW_DELTAS = {
  ArrowRight: 1,
  ArrowLeft: -1,
  ArrowDown: GRID_COLS,
  ArrowUp: -GRID_COLS,
};

const Wrap = styled.div`
  padding: 8px 2px 4px;
  font-family: "DM Sans", system-ui, sans-serif;
`;
const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(${GRID_COLS}, 1fr);
  gap: 8px;
  ${(p) => (p.$busy ? "pointer-events: none;" : "")}
`;
const Tile = styled.div`
  position: relative;
  aspect-ratio: 16 / 11;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  background: #f4f2f8;
  border: ${(p) => (p.$selected ? "2px solid #9B5AF8" : "1px solid #e1e1e1")};
  transition: border-color 120ms ease, transform 120ms ease;
  &:hover {
    border-color: #c9a8ff;
  }
  &:focus-visible {
    outline: none;
    box-shadow: ${focusRing.shadow};
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;
const Img = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;
// Broken-image fallback (NEM F5): a muted line-art placeholder that keeps the tile
// (and the grid layout + roving-focus index) intact instead of the browser's broken
// glyph.
const Broken = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #b9b2c6;
  background: #f4f2f8;
`;
const Check = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #9b5af8;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Note = styled.div`
  font-size: 12px;
  color: #565556;
  margin: 10px 2px 0;
  line-height: 1.4;
`;
// O3: a moderation deny must not be a silent dead end — an inline, terse,
// role="alert" line under the grid tells the user why nothing selected.
const ErrorLine = styled.div`
  font-size: 12px;
  color: #b3261e;
  margin: 10px 2px 0;
  line-height: 1.4;
`;
// Y4: a visible busy state on the in-flight tile (dim + spinner) so a moderation
// round-trip reads as "working", not a frozen picker.
const BusyOverlay = styled.span`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.55);
`;
const Spinner = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid rgba(155, 90, 248, 0.3);
  border-top-color: #9b5af8;
  animation: stock-spin 0.7s linear infinite;
  @keyframes stock-spin {
    to {
      transform: rotate(360deg);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    animation: none;
    border-top-color: rgba(155, 90, 248, 0.3);
  }
`;
const Empty = styled.div`
  font-size: 12px;
  color: #565556;
  padding: 24px 8px;
  text-align: center;
  line-height: 1.5;
`;

// line-art check glyph (never emoji)
const CheckIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" aria-hidden="true">
    <path
      d="M5 12.5l4 4 10-10"
      fill="none"
      stroke="#fff"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// line-art image glyph for the broken-tile placeholder (never emoji)
const BrokenIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="3" y="4" width="18" height="16" rx="2" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <circle cx="8.5" cy="9.5" r="1.6" fill="currentColor" />
    <path d="M4 17l5-5 4 4 3-3 4 4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StockImageLibrary = ({ onSelect, selectedSrc = null, items = STOCK_IMAGE_ITEMS }) => {
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [brokenIds, setBrokenIds] = useState(() => new Set());
  const tileRefs = useRef([]);
  // Synchronous re-entrancy guard (NEM F6). A state flag lags a render behind, so
  // two back-to-back clicks both read the stale (null) value and double-fire; a ref
  // flips immediately and blocks the second call regardless of render timing.
  const busyRef = useRef(false);

  // Roving-tabindex focus index (NEM F1). Initialise onto the currently-selected
  // tile so Tab lands on it; else the first tile.
  const initialActive = useMemo(() => {
    const i = items.findIndex((it) => isStockItemSelected(it, selectedSrc));
    return i >= 0 ? i : 0;
  }, [items, selectedSrc]);
  const [activeIndex, setActiveIndex] = useState(initialActive);

  const focusTile = (idx) => {
    const node = tileRefs.current[idx];
    if (node && typeof node.focus === "function") node.focus();
  };

  const moveFocus = (nextIdx) => {
    const clamped = Math.max(0, Math.min(items.length - 1, nextIdx));
    setActiveIndex(clamped);
    focusTile(clamped);
  };

  const choose = async (item) => {
    if (!onSelect) return;
    // NEM F6: refuse re-entrant selection while a pick is in flight — a later-
    // resolving moderation call must not clobber the last click.
    if (busyRef.current) return;
    busyRef.current = true;
    setError(null);
    setBusyId(item.id);
    try {
      // Route the curated image through the SHARED moderation wrapper (same gate as
      // uploads) before handing it to the consumer — and HONOR the verdict (NEM F2).
      const result = await routeImageThroughModeration({
        image_url: item.src,
        source: MODERATION_SOURCE.STOCK,
      });
      // A gate that cannot say "no" is theatre; respect an explicit deny — and
      // surface it (O3), never a silent dead end.
      if (result && result.approved === false) {
        setError("This image can't be used. Pick another.");
        return;
      }
      onSelect(toStockInsertValue(item));
    } finally {
      busyRef.current = false;
      setBusyId(null);
    }
  };

  const onTileKeyDown = (e, index) => {
    const { key } = e;
    if (SELECT_KEYS.has(key)) {
      e.preventDefault();
      choose(items[index]);
    } else if (key === "Home") {
      e.preventDefault();
      moveFocus(0);
    } else if (key === "End") {
      e.preventDefault();
      moveFocus(items.length - 1);
    } else if (key in ARROW_DELTAS) {
      e.preventDefault();
      moveFocus(index + ARROW_DELTAS[key]);
    }
  };

  const onImgError = (id) => {
    setBrokenIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  // NEM F5: empty state — never a bare heading over an empty listbox.
  if (!items || items.length === 0) {
    return (
      <Wrap>
        <Empty>No preset images are available right now.</Empty>
      </Wrap>
    );
  }

  const busy = busyId != null;

  return (
    <Wrap>
      <Grid role="listbox" aria-label="Preset images" $busy={busy}>
        {items.map((item, index) => {
          const selected = isStockItemSelected(item, selectedSrc);
          const isBroken = brokenIds.has(item.id);
          return (
            <Tile
              key={item.id}
              ref={(el) => (tileRefs.current[index] = el)}
              role="option"
              // Roving tabindex: exactly one tile is tabbable (NEM F1).
              tabIndex={index === activeIndex ? 0 : -1}
              aria-selected={selected}
              aria-label={item.label}
              aria-busy={busyId === item.id}
              aria-disabled={busy || undefined}
              title={item.label}
              $selected={selected}
              data-testid={`stock-image-${item.id}`}
              onFocus={() => setActiveIndex(index)}
              onClick={() => choose(item)}
              onKeyDown={(e) => onTileKeyDown(e, index)}
            >
              {isBroken ? (
                <Broken>
                  <BrokenIcon />
                </Broken>
              ) : (
                <Img
                  src={item.thumbnail}
                  alt={`${item.label} preset image`}
                  loading="lazy"
                  onError={() => onImgError(item.id)}
                />
              )}
              {selected && (
                <Check aria-hidden="true">
                  <CheckIcon />
                </Check>
              )}
              {busyId === item.id && (
                <BusyOverlay aria-hidden="true">
                  <Spinner />
                </BusyOverlay>
              )}
            </Tile>
          );
        })}
      </Grid>
      {error && <ErrorLine role="alert">{error}</ErrorLine>}
      <Note>
        Curated, ready-to-use images. Every image — preset or your own upload — passes
        through content moderation.
      </Note>
    </Wrap>
  );
};

export default StockImageLibrary;
